import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { 
  Car, 
  Printer, 
  Factory, 
  Truck,
  ArrowRight 
} from 'lucide-react';

const Industries = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [activeTab, setActiveTab] = useState('automotive');

  const industryIcons = {
    automotive: <Car className="w-6 h-6" />,
    printing: <Printer className="w-6 h-6" />,
    manufacturing: <Factory className="w-6 h-6" />,
    logistics: <Truck className="w-6 h-6" />
  };

  return (
    <section id="industries" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t.industriesSection.title}
          </h2>
          <p className="text-xl text-gray-600">
            {t.industriesSection.subtitle}
          </p>
        </div>

        <div className="flex flex-wrap justify-center mb-12">
          {Object.entries(t.industries).map(([key, industry]) => (
            <button
              key={key}
              className={`flex items-center px-6 py-3 m-2 rounded-full transition-all ${
                activeTab === key 
                  ? 'text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={activeTab === key ? { backgroundColor: '#115d7b' } : {}}
              onClick={() => setActiveTab(key)}
            >
              <span className="mr-2">
                {industryIcons[key as keyof typeof industryIcons]}
              </span>
              <span className="font-medium">{industry.title}</span>
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {Object.entries(t.industries).map(([key, industry]) => (
            <div 
              key={key}
              className={activeTab === key ? 'block' : 'hidden'}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                {industry.title}
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                {industry.description}
              </p>

              <div className="space-y-6 mb-8">
                {industry.painPoints.map((point, index) => (
                  <div key={index} className="flex">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center mr-4" style={{ backgroundColor: '#e0f2fe' }}>
                      <span className="font-bold" style={{ color: '#115d7b' }}>{index + 1}</span>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">
                        {point.title}
                      </h4>
                      <p className="text-gray-600">
                        {point.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/demo"
                  className="px-6 py-3 text-white rounded-md transition-colors flex items-center justify-center"
                  style={{ backgroundColor: '#115d7b' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                >
                  {t.cta.industryDemo}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
                <a
                  href="/success-cases"
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-center"
                >
                  {t.cta.caseStudy}
                </a>
              </div>
            </div>
          ))}

          <div className="relative">
            {Object.entries(t.industries).map(([key, industry]) => (
              <div 
                key={key}
                className={`rounded-xl overflow-hidden shadow-2xl relative ${activeTab === key ? 'block' : 'hidden'}`}
              >
                <img 
                  src={industry.imageSrc}
                  alt={industry.title}
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex flex-col justify-end p-6">
                  <span className="text-white/80 text-sm mb-1">{t.industriesSection.successStory}</span>
                  <h4 className="text-xl text-white font-semibold mb-2">{industry.testimonial.company}</h4>
                  <p className="text-white/90">{industry.testimonial.quote}</p>
                  <div className="flex items-center mt-4">
                    <div className="w-10 h-10 rounded-full bg-gray-300 mr-3"></div>
                    <div>
                      <div className="text-white font-medium">{industry.testimonial.author}</div>
                      <div className="text-white/80 text-sm">{industry.testimonial.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Industries;